import useMountedEffect from 'hooks/useMountedEffect'
import { clamp } from 'lib/maths/mathUtils'
import { rem } from 'polished'
import { useEffect, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import RangeInput from './RangeInput'

const SliderContainer = styled.div`
  height: ${rem(24)};
  display: flex;
  align-items: center;
  position: relative;
  z-index: 0;
`

const ProgressTrack = styled.div`
  width: 100%;
  height: ${rem(6)};
  background: linear-gradient(
    to right,
    ${props => props.theme.palette.neutral.default.five} 0%,
    ${props => props.theme.palette.brand.primary.normal} 0% var(--end, 0%),
    ${props => props.theme.palette.neutral.default.five} var(--end, 100%)
  );
  border-radius: ${props => props.theme.borderRadius.round};
`

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  min: number
  max: number
  value?: number
  defaultValue?: number
}

function Slider(props: Props) {
  const {
    min,
    max,
    name,
    id,
    className,
    defaultValue,
    value,
    ...inputProps
  } = props

  const progressTrackRef = useRef<HTMLDivElement | null>(null)
  const rangeRef = useRef<HTMLInputElement | null>(null)

  const updateProgressTrack = (value: number) => {
    const totalRange = max - min
    const adjustedValue = value - min

    progressTrackRef.current?.style.setProperty('--end', `${(adjustedValue / totalRange) * 100}%`)
  }

  useMountedEffect(() => {
    if (rangeRef.current) {
    // If min/max changes, make sure our values are still valid and within the ranges
      const nextValue = clamp(min, rangeRef.current?.valueAsNumber, max)
      if (nextValue !== rangeRef.current?.valueAsNumber) {
        rangeRef.current.value = nextValue.toString()
      }
      updateProgressTrack(nextValue)
    }
  }, [min, max])

  useEffect(() => {
    if (rangeRef.current) {
      updateProgressTrack(value ?? rangeRef.current?.valueAsNumber)
    }
  // eslint-disable-next-line
  }, [value])

  return <SliderContainer className={className}>
    <ProgressTrack ref={progressTrackRef} />
    <RangeInput
      {...inputProps}
      ref={rangeRef}
      onChange={(e) => {
        updateProgressTrack(e.currentTarget.valueAsNumber)
        inputProps.onChange?.(e)
      }}
      value={value}
      defaultValue={value === undefined ? (defaultValue ?? min) : undefined}
      min={min}
      max={max}
      name={name ? `${name}::number` : undefined}
    />
  </SliderContainer>
}

export default React.memo(Slider)
